import { CopyToClipboardButton } from '@app-builder/components';
import { Callout } from '@app-builder/components/Callout';
import { ExternalLink } from '@app-builder/components/ExternalLink';
import { Nudge } from '@app-builder/components/Nudge';
import { Page } from '@app-builder/components/Page';
import { pageLayoutGutter } from '@app-builder/components/Page/page-layout';
import { CreateTestRun } from '@app-builder/components/Scenario/Actions/CreateTestRun';
import { CreateDraftIteration } from '@app-builder/components/Scenario/Iteration/Actions/CreateDraft';
import { ScenarioDescriptionEditable, ScenarioHeader } from '@app-builder/components/Scenario/ScenarioHeader';
import { TestRunNudge } from '@app-builder/components/Scenario/TestRun/TestRunNudge';
import { Spinner } from '@app-builder/components/Spinner';
import { WorkflowNudge } from '@app-builder/components/Workflows/Nudge';
import { useLoaderRevalidator } from '@app-builder/contexts/LoaderRevalidatorContext';
import { useDetectionScenarioData } from '@app-builder/hooks/routes-layout-data';
import { authMiddleware } from '@app-builder/middlewares/auth-middleware';
import { hasInProgressScheduledExecution, type ScheduledExecution } from '@app-builder/models/decision';
import { type Scenario } from '@app-builder/models/scenario';
import { type ScenarioIterationSummaryWithType } from '@app-builder/models/scenario/iteration';
import { useToggleScenarioDeduplicationMutation } from '@app-builder/queries/scenarios/toggle-deduplication';
import { useListRulesQuery } from '@app-builder/queries/Workflows';
import { createDecisionDocHref } from '@app-builder/services/documentation-href';
import { isEditScenarioAvailable, isManualTriggerScenarioAvailable } from '@app-builder/services/feature-access';
import { formatSchedule } from '@app-builder/utils/format';
import { fromParams, fromUUIDtoSUUID } from '@app-builder/utils/short-uuid';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { type ParseKeys } from 'i18next';
import { type FeatureAccessLevelDto } from 'marble-api/generated/feature-access-api';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import { Button, Checkbox, CtaV2ClassName, cn, Separator, Switch, Tooltip, Typo } from 'ui-design-system';
import { Icon } from 'ui-icons';
import { z } from 'zod/v4';

const homeLoader = createServerFn()
  .middleware([authMiddleware])
  .validator((input: { params?: Record<string, string> } | undefined) => input)
  .handler(async function homeLoader({ data, context }) {
    const { user, entitlements, decision, testRun: testRunRepository, scenario } = context.authInfo;

    const scenarioId = fromParams(data?.params ?? {}, 'scenarioId');

    const currentScenario = await scenario.getScenario({ scenarioId });

    const [scheduledExecutions, testRuns, liveIteration] = await Promise.all([
      decision.listScheduledExecutions({ scenarioId }),
      testRunRepository.listTestRuns({ scenarioId }),
      currentScenario.liveVersionId
        ? scenario.getScenarioIterationWithoutRules({ iterationId: currentScenario.liveVersionId })
        : Promise.resolve(null),
    ]);

    return {
      featureAccess: {
        isEditScenarioAvailable: isEditScenarioAvailable(user),
        isManualTriggerScenarioAvailable: isManualTriggerScenarioAvailable(user),
        isWorkflowsAvailable: entitlements.workflows,
        isTestRunAvailable: entitlements.testRun,
      },
      scheduledExecutions,
      testRuns,
      liveIterationSchedule: liveIteration?.schedule,
    };
  });

const triggerManualExecutionPayloadSchema = z.object({
  params: z.object({ iterationId: z.string() }),
  forceRescore: z.boolean().optional(),
});

const triggerManualExecutionAction = createServerFn()
  .middleware([authMiddleware])
  .validator(triggerManualExecutionPayloadSchema)
  .handler(async function triggerManualExecutionAction({ data, context }) {
    const { scenario } = context.authInfo;
    await scenario.scheduleScenarioExecution({
      iterationId: data.params.iterationId,
      deduplicateObjects: data.forceRescore ? false : undefined,
    });
    return { status: 'success' as const };
  });

export const Route = createFileRoute('/_app/_builder/detection/scenarios/$scenarioId/home')({
  loader: ({ params }) => homeLoader({ data: { params } }),
  component: ScenarioHome,
});

function ScenarioHome() {
  const { t } = useTranslation(['common', 'scenarios']);
  const { featureAccess, scheduledExecutions, liveIterationSchedule } = Route.useLoaderData();

  const { scenarioIterations, currentScenario } = useDetectionScenarioData();

  const liveScenarioIteration = useMemo(
    () => scenarioIterations.find(({ type }) => type === 'live version'),
    [scenarioIterations],
  );

  const lastScenarioIteration = useMemo(
    () =>
      scenarioIterations
        .filter(({ type }) => type === 'version')
        .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0],
    [scenarioIterations],
  );

  const draftScenario = useMemo(() => scenarioIterations.find(({ type }) => type === 'draft'), [scenarioIterations]);

  const scenarioToWatch = liveScenarioIteration ?? lastScenarioIteration;
  const scenarioToEdit = liveScenarioIteration ?? lastScenarioIteration;
  const canEditScenario = featureAccess.isEditScenarioAvailable;

  return (
    <Page.Main>
      <Page.Header className="justify-between">
        <div className="flex flex-row items-center gap-xs">
          <ScenarioHeader isEditScenarioAvailable={canEditScenario} scenario={currentScenario} />
        </div>
      </Page.Header>
      <Page.Content width="readable">
        {currentScenario.archived ? (
          <Callout color="red" icon="warning" className="mb-md">
            {t('scenarios:archived_scenario_banner')}
          </Callout>
        ) : null}
        <section className="flex flex-row gap-lg items-center">
          {currentScenario.description || canEditScenario ? (
            <Page.Description withIcon={false} className="flex-1">
              <ScenarioDescriptionEditable isEditScenarioAvailable={canEditScenario} scenario={currentScenario} />
            </Page.Description>
          ) : null}
          <div className="flex flex-row gap-md">
            {scenarioToWatch ? (
              <Link
                to="/detection/scenarios/$scenarioId/i/$iterationId"
                params={{
                  scenarioId: fromUUIDtoSUUID(scenarioToWatch.scenarioId),
                  iterationId: fromUUIDtoSUUID(scenarioToWatch.id),
                }}
                className={CtaV2ClassName({ variant: 'primary', appearance: 'stroked', size: 'medium' })}
              >
                <Icon icon="eye" className="size-4" />
                {liveScenarioIteration ? t('scenarios:home.live_version') : t('scenarios:home.last_version')}
              </Link>
            ) : null}
            {canEditScenario && draftScenario ? (
              <Link
                to="/detection/scenarios/$scenarioId/i/$iterationId"
                params={{
                  scenarioId: fromUUIDtoSUUID(draftScenario.scenarioId),
                  iterationId: fromUUIDtoSUUID(draftScenario.id),
                }}
                className={CtaV2ClassName({ variant: 'primary', appearance: 'filled', size: 'medium' })}
              >
                <Icon icon="edit" className="size-4" />
                {t('scenarios:update_scenario.title')}
              </Link>
            ) : null}
            {canEditScenario && !draftScenario && scenarioToEdit ? (
              <CreateDraftIteration
                iterationId={scenarioToEdit.id}
                scenarioId={currentScenario.id}
                draftId={undefined}
              />
            ) : null}
          </div>
        </section>
        <section className={cn('flex flex-col', pageLayoutGutter.gap)}>
          <Typo variant="title2" className="text-grey-primary">
            {t('scenarios:home.execution')}
          </Typo>
          <div className={cn('grid grid-cols-2', pageLayoutGutter.gap)}>
            <RealTimeSection scenarioId={currentScenario.id} liveScenarioIteration={liveScenarioIteration} />
            <BatchSection
              currentScenario={currentScenario}
              scenarioId={currentScenario.id}
              isManualTriggerScenarioAvailable={featureAccess.isManualTriggerScenarioAvailable}
              scheduledExecutions={scheduledExecutions}
              liveIterationSchedule={liveIterationSchedule}
              liveIterationId={liveScenarioIteration?.id}
            />
            {match(featureAccess.isTestRunAvailable)
              .with('missing_configuration', (status) => <TestRunNudge kind={status} />)
              .with('restricted', (status) => <TestRunNudge kind={status} />)
              .otherwise(() => (
                <TestRunSection scenarioId={currentScenario.id} access={featureAccess.isTestRunAvailable} />
              ))}
            {match(featureAccess.isWorkflowsAvailable)
              .with('missing_configuration', (status) => <WorkflowNudge kind={status} />)
              .with('restricted', (status) => <WorkflowNudge kind={status} />)
              .otherwise(() => (
                <WorkflowSection scenario={currentScenario} access={featureAccess.isWorkflowsAvailable} />
              ))}
          </div>
        </section>
        <ResourcesSection />
      </Page.Content>
    </Page.Main>
  );
}

function TestRunSection({ scenarioId, access }: { scenarioId: string; access: FeatureAccessLevelDto }) {
  const { t } = useTranslation();
  const { currentScenario, scenarioIterations } = useDetectionScenarioData();
  const { testRuns } = Route.useLoaderData();

  const currentTestRun = useMemo(() => testRuns.filter((r) => r.status === 'up'), [testRuns]);

  const isExecutionOngoing = useMemo(() => currentTestRun.length > 0, [currentTestRun]);

  return (
    <article className="flex flex-col">
      <TabHeader title={t('scenarios:home.testrun')} spinner={isExecutionOngoing} />
      <section
        className={cn(
          'bg-surface-card border-grey-border relative flex flex-col gap-md rounded-lg border p-md rounded-tl-none flex-1',
          isExecutionOngoing && 'border-purple-primary',
        )}
      >
        {access === 'test' ? (
          <Nudge
            className="absolute -end-[12px] -top-[12px] size-6"
            content={t('scenarios:testrun.nudge')}
            kind="test"
          />
        ) : null}

        <p>{t('scenarios:testrun.description')}</p>

        <div className="flex flex-row gap-md mt-auto">
          <CreateTestRun
            currentScenario={currentScenario}
            scenarioIterations={scenarioIterations}
            atLeastOneActiveTestRun={currentTestRun.length > 0}
          >
            <Button variant="primary" appearance="stroked" className="isolate">
              <Icon icon="plus" className="size-4" aria-hidden />
              {t('scenarios:create_testrun.title')}
            </Button>
          </CreateTestRun>
          {currentTestRun.length > 0 ? (
            <Link
              className={CtaV2ClassName({ variant: 'secondary' })}
              to="/detection/scenarios/$scenarioId/test-run/$testRunId"
              params={{
                scenarioId: fromUUIDtoSUUID(scenarioId),
                testRunId: fromUUIDtoSUUID(currentTestRun[0]!.id),
              }}
            >
              {t('scenarios:testrun.current_run')}
            </Link>
          ) : null}

          {testRuns.length ? (
            <Link
              className={CtaV2ClassName({ variant: 'secondary' })}
              to="/detection/scenarios/$scenarioId/test-run"
              params={{ scenarioId: fromUUIDtoSUUID(scenarioId) }}
            >
              {t('scenarios:testrun.archived')}
            </Link>
          ) : null}
        </div>
      </section>
    </article>
  );
}

function RealTimeSection({
  scenarioId,
  liveScenarioIteration,
}: {
  scenarioId: string;
  liveScenarioIteration?: ScenarioIterationSummaryWithType;
}) {
  const { t } = useTranslation(['scenarios']);
  const isLive = liveScenarioIteration !== undefined;

  return (
    <article className="flex flex-col">
      <TabHeader title={t('scenarios:home.execution.real_time')} spinner={false} />
      <div className="bg-surface-card border-grey-border flex flex-1 flex-col gap-md rounded-lg border p-md rounded-tl-none">
        <p className="flex flex-col gap-md">
          <span>
            <Trans
              t={t}
              i18nKey="scenarios:home.execution.real_time.callout"
              components={{
                DocLink: <ExternalLink href={createDecisionDocHref} />,
              }}
            />
          </span>
          <span
            className={cn('text-grey-primary text-s inline-flex items-center font-semibold', {
              'whitespace-pre': isLive,
            })}
          >
            {isLive ? (
              <Trans
                t={t}
                i18nKey="scenarios:home.execution.real_time.callout.scenario_id"
                components={{
                  CopyScenarioId: (
                    <CopyToClipboardButton toCopy={scenarioId} className="ms-xs">
                      <code>{`ID ${scenarioId.slice(0, 15)}...`}</code>
                    </CopyToClipboardButton>
                  ),
                }}
              />
            ) : (
              t('scenarios:home.execution.real_time.callout.no_live_version')
            )}
          </span>
        </p>
      </div>
    </article>
  );
}

function TabHeader({ title, spinner }: { title: string; spinner: boolean }) {
  return (
    <div className="flex items-center gap-md border-l border-t border-r bg-surface-card border-grey-border rounded-t-md py-xs px-sm w-fit">
      <Typo variant="subtitle1" className="text-grey-secondary">
        {title}
      </Typo>
      {spinner ? <Spinner className="size-3" /> : null}
    </div>
  );
}

function BatchSection({
  currentScenario,
  scenarioId,
  scheduledExecutions,
  liveIterationSchedule,
  liveIterationId,
  isManualTriggerScenarioAvailable,
}: {
  currentScenario: Scenario;
  scenarioId: string;
  scheduledExecutions: ScheduledExecution[];
  liveIterationSchedule?: string;
  liveIterationId?: string;
  isManualTriggerScenarioAvailable: boolean;
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation(['common', 'scenarios']);
  const revalidate = useLoaderRevalidator();
  const toggleDeduplicationMutation = useToggleScenarioDeduplicationMutation();

  const isLive = !!liveIterationId;
  const schedule = liveIterationSchedule;

  const formattedSchedule = useMemo(() => {
    try {
      if (!schedule) return undefined;
      return formatSchedule(schedule, {
        language,
        throwExceptionOnParseError: true,
      });
    } catch (_e) {
      return undefined;
    }
  }, [language, schedule]);

  const isExecutionOngoing = hasInProgressScheduledExecution(scheduledExecutions);

  const handleToggleDeduplicate = (checked: boolean) => {
    toggleDeduplicationMutation.mutate(
      { scenarioId: currentScenario.id, enabled: checked },
      {
        onSuccess: () => {
          toast.success(t('common:success.save'));
          revalidate();
        },
        onError: () => {
          toast.error(t('common:errors.unknown'));
        },
      },
    );
  };

  return (
    <article className="flex flex-col">
      <TabHeader title={t('scenarios:home.execution.batch')} spinner={isExecutionOngoing} />
      <div
        className={cn(
          'bg-surface-card border-grey-border relative flex flex-1 flex-col gap-md rounded-lg border p-md rounded-tl-none',
          isExecutionOngoing && 'border-purple-primary',
        )}
      >
        <div className="flex flex-row items-center gap-md">
          <Tooltip.Default
            content={t('scenarios:home.execution.batch.deduplicate_batch_objects_tooltip')}
            arrow={true}
            delayDuration={0}
          >
            <label htmlFor="deduplicate-toggle" className="flex flex-row items-center gap-sm cursor-pointer">
              <Switch
                id="deduplicate-toggle"
                checked={currentScenario.deduplicateBatchObjects}
                onCheckedChange={handleToggleDeduplicate}
                disabled={toggleDeduplicationMutation.isPending}
              />
              <span className="text-sm font-medium">
                {t('scenarios:home.execution.batch.deduplicate_batch_objects')}
              </span>
            </label>
          </Tooltip.Default>
        </div>

        <Separator className="bg-grey-border" />

        <p className="flex flex-col gap-md">
          <span>{t('scenarios:home.execution.batch.callout')}</span>
          {formattedSchedule ? (
            <span className="text-grey-primary text-s text-balance font-semibold">
              <Trans
                t={t}
                i18nKey="scenarios:scheduled"
                components={{
                  ScheduleLocale: <span className="text-purple-primary" />,
                }}
                values={{
                  schedule: formattedSchedule,
                }}
              />
            </span>
          ) : null}
        </p>

        <div className="flex flex-col gap-md">
          {isManualTriggerScenarioAvailable && isLive && liveIterationId ? (
            <ManualTriggerScenarioExecutionForm
              iterationId={liveIterationId}
              scenario={currentScenario}
              disabled={isExecutionOngoing}
            />
          ) : null}
        </div>

        <Separator className="bg-grey-border" />

        <Link
          className={CtaV2ClassName({ variant: 'secondary' })}
          to="/detection/scenarios/$scenarioId/scheduled-executions"
          params={{ scenarioId: fromUUIDtoSUUID(scenarioId) }}
        >
          {t('scenarios:home.execution.batch.scheduled_execution', {
            count: scheduledExecutions.length,
          })}
        </Link>
      </div>
    </article>
  );
}

function ManualTriggerScenarioExecutionForm({
  iterationId,
  scenario,
  disabled,
}: {
  iterationId: string;
  scenario: Scenario;
  disabled: boolean;
}) {
  const { t } = useTranslation(['scenarios']);
  const revalidate = useLoaderRevalidator();

  const mutation = useMutation({
    mutationFn: async (value: { iterationId: string; forceRescore?: boolean }) => {
      return triggerManualExecutionAction({
        data: { params: { iterationId: value.iterationId }, forceRescore: value.forceRescore },
      });
    },
    onSuccess: () => {
      revalidate();
    },
  });

  const form = useForm({
    onSubmit: ({ value, formApi }) => {
      if (formApi.state.isValid) {
        mutation.mutate(value);
      }
    },
    defaultValues: { iterationId, forceRescore: false },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="flex flex-row items-center gap-md">
        <Button
          type="submit"
          disabled={disabled || mutation.isPending}
          appearance="stroked"
          className="shrink-0 whitespace-nowrap"
        >
          <Icon icon="play" className="size-4 shrink-0" aria-hidden />
          {t('scenarios:home.execution.batch.trigger_manual_execution')}
        </Button>
        {scenario.deduplicateBatchObjects ? (
          <form.Field name="forceRescore">
            {(field) => (
              <label htmlFor="force-rescore" className="flex flex-row items-center gap-sm cursor-pointer">
                <Checkbox
                  size="small"
                  checked={field.state.value}
                  onCheckedChange={(checked) => {
                    if (checked === 'indeterminate') return;
                    field.handleChange(checked);
                  }}
                  onBlur={field.handleBlur}
                  id="force-rescore"
                />
                <span className="text-sm">{t('scenarios:home.execution.batch.force_rescore_all_objects')}</span>
              </label>
            )}
          </form.Field>
        ) : null}
      </div>
    </form>
  );
}

function WorkflowSection({ scenario, access }: { scenario: Scenario; access: FeatureAccessLevelDto }) {
  const { t } = useTranslation(['common', 'scenarios', 'workflows']);

  const rulesQuery = useListRulesQuery(scenario.id);
  const isEdit = (rulesQuery.data?.workflow?.length ?? 0) > 0;

  let tag: string | undefined;
  let tooltip: string | undefined;

  return (
    <article className="flex flex-col">
      <TabHeader title={t('scenarios:home.workflow')} spinner={false} />
      <section className="bg-surface-card border-grey-border relative flex flex-col gap-md rounded-lg border p-md rounded-tl-none flex-1">
        {access === 'test' ? (
          <Nudge
            className="absolute -end-[12px] -top-[12px] size-6"
            content={t('workflows:nudge')}
            link="https://docs.checkmarble.com/docs/introduction-5"
            kind="test"
          />
        ) : null}

        <p>{t('scenarios:home.workflow_description')}</p>

        <div className="flex flex-row gap-md">
          {tag ? (
            <div className="bg-purple-background-light text-s text-purple-primary flex h-10 flex-row items-center gap-sm rounded-sm px-xs uppercase">
              {tag}
              {tooltip ? (
                <Tooltip.Default
                  arrow={false}
                  delayDuration={0}
                  side="right"
                  sideOffset={8}
                  content={tooltip}
                  className="border-grey-border flex w-fit max-w-80 border shadow-md"
                >
                  <button
                    type="button"
                    aria-label={t('scenarios:home.workflow_type.tooltip_aria_label')}
                    className="text-purple-disabled hover:text-purple-primary cursor-pointer transition-colors"
                  >
                    <Icon icon="tip" className="size-5" />
                  </button>
                </Tooltip.Default>
              ) : null}
            </div>
          ) : null}

          {rulesQuery.isLoading ? (
            <div className="bg-grey-border h-7 w-30 animate-pulse rounded-md flex items-center gap-xs px-sm">
              <div className="bg-grey-disabled size-3.5 animate-pulse rounded-sm" />
              <div className="bg-grey-disabled h-4 w-16 animate-pulse rounded-sm" />
            </div>
          ) : (
            <Link
              className={CtaV2ClassName({ variant: isEdit ? 'secondary' : 'primary', appearance: 'stroked' })}
              to="/detection/scenarios/$scenarioId/workflow"
              params={{ scenarioId: fromUUIDtoSUUID(scenario.id) }}
            >
              <Icon icon={isEdit ? 'edit-square' : 'plus'} className="size-4" />
              <p>{t(isEdit ? 'scenarios:home.workflow.edit' : 'scenarios:home.workflow.create')}</p>
            </Link>
          )}
        </div>
      </section>
    </article>
  );
}

const resources = [
  {
    tKey: 'scenarios:home.resources.scenario_guide',
    href: 'https://docs.checkmarble.com/docs/executing-a-scenario',
    src: '/img/home/scenario-guide.png',
  },
  {
    tKey: 'scenarios:home.resources.api',
    href: 'https://docs.checkmarble.com/reference/intro-getting-started',
    src: '/img/home/api.png',
  },
  {
    tKey: 'scenarios:home.workflow',
    href: 'https://docs.checkmarble.com/docs/introduction-5',
    src: '/img/home/workflow.png',
  },
] satisfies Array<{
  tKey: ParseKeys<['scenarios']>;
  href: string;
  src: string;
}>;

function ResourcesSection() {
  const { t } = useTranslation(['common', 'scenarios']);
  return (
    <section className="flex flex-col gap-sm mx-auto">
      <Typo variant="title2" className="text-grey-primary">
        {t('scenarios:home.resources')}
      </Typo>
      <div className="flex flex-row gap-sm">
        {resources.map(({ tKey, href, src }) => (
          <a
            key={tKey}
            href={href}
            className="border-grey-border hover:border-purple-primary focus:border-purple-primary group flex flex-col overflow-hidden rounded-lg border outline-hidden transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={src} alt="" className="aspect-[21/9] object-cover" />
            <span className="border-grey-border bg-surface-card text-s group-hover:border-purple-primary group-focus:border-purple-primary flex flex-row items-center justify-between border-t p-sm font-medium transition-colors">
              {t(tKey)}
              <Icon aria-hidden icon="arrow-right" className="size-3.5" />
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}
